import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ReactFlow,
  Background,
  Controls,
  type Edge,
  type Node,
  MarkerType,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { branchApi, type StoryBranch } from '@/api/branchApi'
import { friendlyMessage } from '@/api/http'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'

interface BranchGraphProps {
  projectId: string
}

const NODE_W = 200
const COL_GAP = 280
const ROW_GAP = 110

// 按 parent_branch_id 把分支排成树, 计算每个节点的 (depth, row) → 像素坐标。
function layoutBranches(branches: StoryBranch[]): Map<string, { x: number; y: number }> {
  const byParent = new Map<string | null, StoryBranch[]>()
  for (const b of branches) {
    const key = b.parent_branch_id ?? null
    const list = byParent.get(key) ?? []
    list.push(b)
    byParent.set(key, list)
  }
  const pos = new Map<string, { x: number; y: number }>()
  let nextRow = 0
  // roots = 无 parent 或 parent 不在集合内
  const ids = new Set(branches.map((b) => b.id))
  const roots = branches.filter((b) => !b.parent_branch_id || !ids.has(b.parent_branch_id))
  const visit = (node: StoryBranch, depth: number) => {
    const row = nextRow++
    pos.set(node.id, { x: depth * COL_GAP, y: row * ROW_GAP })
    const children = (byParent.get(node.id) ?? []).sort((a, b) => a.fork_chapter - b.fork_chapter)
    for (const child of children) visit(child, depth + 1)
  }
  for (const root of roots) visit(root, 0)
  return pos
}

export function BranchGraph({ projectId }: BranchGraphProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [promoteTarget, setPromoteTarget] = useState<StoryBranch | null>(null)

  const branchesQuery = useQuery({
    queryKey: ['branches', projectId],
    queryFn: () => branchApi.list(projectId),
    enabled: Boolean(projectId),
  })

  const switchMutation = useMutation({
    mutationFn: (branchId: string) => branchApi.switch(projectId, branchId),
    onSuccess: async () => {
      toast.success(t('branch.switched'))
      await queryClient.invalidateQueries({ queryKey: ['branches', projectId] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('branch.switchFailed')))
    },
  })

  const promoteMutation = useMutation({
    mutationFn: (branchId: string) => branchApi.promote(projectId, branchId),
    onSuccess: async () => {
      toast.success(t('branch.promoted'))
      setPromoteTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['branches', projectId] })
    },
    onError: (error) => {
      toast.error(friendlyMessage(error, t('branch.promoteFailed')))
    },
  })

  const branches = useMemo(() => branchesQuery.data ?? [], [branchesQuery.data])
  const promotableBranch = useMemo(
    () => branches.find((branch) => branch.is_active && branch.id !== 'main') ?? null,
    [branches],
  )

  const { nodes, edges } = useMemo(() => {
    const pos = layoutBranches(branches)
    const nodes: Node[] = branches.map((b) => {
      const p = pos.get(b.id) ?? { x: 0, y: 0 }
      return {
        id: b.id,
        position: p,
        data: {
          label: (
            <div className="text-left">
              <div className="flex items-center gap-1.5 font-medium">
                {b.is_active ? <span className="text-primary">● HEAD</span> : null}
                <span className="truncate">{b.name || b.id}</span>
              </div>
              <div className="nf-mono mt-0.5 text-[10px] text-muted-foreground">
                {t('branch.forkAt', { value: b.fork_chapter })}
                {b.direction ? ` · ${b.direction}` : ''}
              </div>
            </div>
          ),
        },
        style: {
          width: NODE_W,
          borderRadius: 8,
          border: b.is_active ? '2px solid var(--primary)' : '1px solid var(--border)',
          background: b.is_active ? 'color-mix(in oklch, var(--primary) 12%, var(--background))' : 'var(--card)',
          fontSize: 12,
          padding: 8,
        },
      }
    })
    const edges: Edge[] = branches
      .filter((b) => b.parent_branch_id)
      .map((b) => ({
        id: `${b.parent_branch_id}->${b.id}`,
        source: b.parent_branch_id as string,
        target: b.id,
        type: 'smoothstep',
        animated: b.is_active,
        markerEnd: { type: MarkerType.ArrowClosed },
      }))
    return { nodes, edges }
  }, [branches, t])

  if (branchesQuery.isLoading) return <Skeleton className="h-[420px] w-full rounded-lg" />
  if (branchesQuery.error) {
    return (
      <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {friendlyMessage(branchesQuery.error, t('branch.loadFailed'))}
      </div>
    )
  }
  if (!branches.length) {
    return (
      <div className="rounded-lg border border-dashed bg-background/45 p-6 text-center text-sm text-muted-foreground">
        {t('branch.empty')}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-background/60 px-3 py-2">
        <div className="min-w-0">
          <div className="truncate text-sm font-medium">
            {t('branch.activeBranch', {
              value: branches.find((branch) => branch.is_active)?.name ?? branches.find((branch) => branch.is_active)?.id ?? 'main',
            })}
          </div>
          <div className="truncate text-xs text-muted-foreground">{t('branch.promoteHint')}</div>
        </div>
        <Button
          type="button"
          size="sm"
          variant="destructive"
          disabled={!promotableBranch || promoteMutation.isPending}
          onClick={() => {
            if (promotableBranch) setPromoteTarget(promotableBranch)
          }}
        >
          {promoteMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <AlertTriangle className="size-4" />}
          {t('branch.promote')}
        </Button>
      </div>
      <div className="h-[calc(100vh-19rem)] min-h-[420px] w-full rounded-lg border">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          fitView
          nodesDraggable={false}
          nodesConnectable={false}
          onNodeClick={(_, node) => {
            const target = branches.find((b) => b.id === node.id)
            if (target && !target.is_active && !switchMutation.isPending) {
              switchMutation.mutate(target.id)
            }
          }}
        >
          <Background />
          <Controls showInteractive={false} />
        </ReactFlow>
      </div>
      <Dialog
        open={Boolean(promoteTarget)}
        onOpenChange={(open) => {
          if (!open && !promoteMutation.isPending) setPromoteTarget(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('branch.promoteConfirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('branch.promoteConfirmDescription', { name: promoteTarget?.name || promoteTarget?.id || '' })}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 rounded-md border border-destructive/35 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" />
            <span>{t('branch.promoteIrreversible')}</span>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={promoteMutation.isPending}
              onClick={() => setPromoteTarget(null)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={!promoteTarget || promoteMutation.isPending}
              onClick={() => {
                if (promoteTarget) promoteMutation.mutate(promoteTarget.id)
              }}
            >
              {promoteMutation.isPending ? <Loader2 className="size-4 animate-spin" /> : <AlertTriangle className="size-4" />}
              {t('branch.promoteConfirmAction')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
